<script lang="ts" setup>
import { OrderControlTableContentViewModel } from './orderControlTableContentViewModel';
import { OrderControlHeaderKey, TableContentProperty } from '../../interface/tableContentHeaders';
import TableCell from '../TableCell.vue';
import RecommendTag from '@/corePagesModule/components/RecommendTag.vue';
import { TableContentItem } from '../../interface/tableContentItems';
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { IOrderControl } from '@/orderControlModule/entities/IOrderControl';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import { computed } from 'vue';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';

/**
 * 編集可能セルの種類
 */
type EditableCell = 'OrderNumber' | 'OrderRecommendCrossDate' | 'OrderRecommendAmount';

// props
const props = withDefaults(
  defineProps<{
    header: TableContentProperty;
    orderControlHeaderKey: OrderControlHeaderKey;
    item: TableContentItem;
    headerIndex: number;
    accumulationTableContentWidth: number;
    isVariableWidth: boolean;
    isAllSelected: boolean;
    stableColumnCount: number;
    isBold: boolean;
    isEditing: boolean;
    isChecked: boolean;
    isCheckedBusinessId: boolean;
  }>(),
  {}
);

// emits
const emits = defineEmits<{
  (e: 'changeEditableValue', headerKey: EditableCell, oldValue: string, newValue: string): void;
  (e: 'onClickNarrow'): void;
  (e: 'onCheckboxSelected', displayId: string): void;
}>();

const OrderControlTableContentVM = new OrderControlTableContentViewModel();
const t = i18n.t;

/**
 * ページ遷移を行う処理
 * TODO: 新IBusinessへの以降処理前でごちゃごちゃするのを避けるため、一旦旧IBusinessの型で置いている
 * 本当はstoreに括り出すべきでは？
 * @param event -  取引情報
 */
const sendPageMethod = (event: TableContentItem) => {
  const order = event as IOrderControl;
  // NOTE: 通常のBusinessIdの場合はSplitをする必要がないが、Arrivalから変換されたBusinessのIdの場合は、BusinessId-ArrivalIdの形式になっているため、'-'でSplitする'
  window.open(`#/business/recommend-order/${order ? order.aggregateBusinessId : undefined}`, '_blank');
};

const textAlign = computed(() => {
  if (props.header.key === 'OrderControlChoice') {
    return 'center';
  }
  return props.header.propertyPath?.propertyType === 'number' ? 'right' : 'left';
});

/**
 * 発注が推奨発注かつ、発注日が当日以前の場合、太字の赤色で表示する
 * @param header - ヘッダー情報
 * @param item - 発注情報
 */
const isRedAndBold = (header: string, item: TableContentItem) => {
  const createdOrderDate = new Date(item.createdOrderDate * 1000);
  const nowDate = new Date();
  createdOrderDate.setHours(0, 0, 0, 0);
  nowDate.setHours(0, 0, 0, 0);
  return header === 'OrderControlOrderDate' && item.orderStatus === OrderStatus.Recommended && createdOrderDate <= nowDate;
};
</script>

<template>
  <TableCell
    :class="[OrderControlTableContentVM.isSticky(stableColumnCount, headerIndex) ? 'sticky-content' : '']"
    :style="{
      left: OrderControlTableContentVM.calcStickyDiff(stableColumnCount, headerIndex, accumulationTableContentWidth),
    }"
    class="table-cell base-table__body--fixed"
    :text-align="textAlign"
    :table-content-property="header"
    :tool-tip-value="OrderControlTableContentVM.displayOrderItem(orderControlHeaderKey, item)"
    :is-highlighted="props.isChecked || props.isCheckedBusinessId"
  >
    <template v-if="orderControlHeaderKey === 'OrderConfirmation'">
      <TextButton v-if="!item.virtualChild" class="detail-button" button-style="btn-secondary" :on-click="() => sendPageMethod(item)">
        {{ t('詳細') }}
      </TextButton>
    </template>
    <template v-if="orderControlHeaderKey === 'OrderControlOrderStatus'">
      <RecommendTag :status="item.orderStatus" />
    </template>
    <template v-else-if="orderControlHeaderKey === 'OrderControlChoice'">
      <RowCheckBox
        v-if="!isEditing && !item.isVirtualChild"
        :is-checked="isChecked"
        class="md-primary all-check-button"
        @change-status="emits('onCheckboxSelected', item?.displayId)"
      />
    </template>
    <template v-else>
      <span
        class="table-cell-content"
        :class="{
          'table-cell-content--bold': isBold || isRedAndBold(orderControlHeaderKey, item),
          'table-cell-content--red': isRedAndBold(orderControlHeaderKey, item),
        }"
        @click="emits('onClickNarrow')"
      >
        {{ OrderControlTableContentVM.displayOrderItem(orderControlHeaderKey, item) }}
      </span>
    </template>
  </TableCell>
</template>

<style lang="scss" scoped>
.table-cell-content {
  font-family: Noto Sans JP;
  font-size: $fontSizeSS;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &--bold {
    font-weight: bold;
  }

  &--red {
    color: $semanticError;
  }
}

.detail-button {
  height: fit-content;
  font-size: 10px;
  line-height: 10px;
  font-weight: 700;
  text-align: center;
  padding: 3px 15px;
}
</style>
